import type { ContactFormStep } from './constants';
import type {
	ContactFormContext,
	ContactFormModalProps
} from '~/components/ContactFormModal';

import { useAdCampaign } from '~/composables/adCampaign';
import { useContactForm } from '~/composables/useContactForm';
import { getAgentFirstName, getAgentName } from '~~/utils';

import { CONTACT_FORM_STEPS } from './constants';

const CURRENT_PAGE_BY_CONTEXT: Record<ContactFormContext, string> = {
	property: 'Detalhe do Imóvel',
	condominium: 'Detalhe do Condomínio',
	agent: 'Perfil do Corretor'
};

interface UseContactFormModalOptions {
	t: (key: string, options?: Record<string, string | undefined>) => string;
}

export function useContactFormModal(
	props: ContactFormModalProps,
	options: UseContactFormModalOptions
) {
	const { t } = options;
	const { getAdCampaign } = useAdCampaign();
	const {
		loggedIn: isUserLoggedIn,
		user,
		fetch: fetchUserSession
	} = useUserSession();

	const form = useContactForm();

	const currentStep = ref<ContactFormStep>(CONTACT_FORM_STEPS.CONTACT_FORM);
	const errorMessage = ref<string | null>(null);
	const isSubmittingOtpForm = shallowRef<boolean>(false);
	const isOTPValid = shallowRef<boolean | null>(null);
	const isSubmittingForm = shallowRef<boolean>(false);

	const isPropertyContext = computed(() => props.context === 'property');

	const shouldShowBrokerInfo = computed(
		() => props.context === 'property' || props.context === 'agent'
	);
	const currentPageName = computed(() => {
		const override = props.analyticsCurrentPage?.trim();
		if (override) return props.analyticsCurrentPage as string;
		return CURRENT_PAGE_BY_CONTEXT[props.context];
	});
	const shouldExpandContainer = computed(
		() => !!(form.name.value?.trim() && form.phone.value?.trim())
	);
	const brokerFirstName = computed(() => getAgentFirstName(props.broker));
	const brokerName = computed(() => getAgentName(props.broker));
	const getAccountStatus = computed(() =>
		isUserLoggedIn.value ? 'logged in' : 'logged out'
	);

	const initialUserMessage = computed<string>(() => {
		const translateByContext = (
			key: string,
			options: Record<string, string | undefined> = {}
		) => {
			const baseKey = `${props.context}.${key}`;
			return t(baseKey, options);
		};

		const optionsByContext: Record<
			ContactFormContext,
			Record<string, string | undefined>
		> = {
			property: {
				propertyCommercialId: props.propertyCommercialId,
				propertyRegion: props.propertyRegion,
				propertyType: props.propertyTypeName?.toLowerCase() || ''
			},
			condominium: { condoName: props.condoName },
			agent: { brokerName: getAgentFirstName(props.broker) }
		};

		return translateByContext(
			'form.userMessage.initialValue',
			optionsByContext[props.context]
		);
	});

	return {
		// State refs
		currentStep,
		errorMessage,
		isSubmittingOtpForm,
		isSubmittingForm,
		isOTPValid,

		// Computed refs
		isPropertyContext,
		shouldShowBrokerInfo,
		currentPageName,
		shouldExpandContainer,
		brokerFirstName,
		brokerName,
		getAccountStatus,
		initialUserMessage,

		// User refs
		isUserLoggedIn,
		user,

		// Form field values (already computed)
		name: form.name,
		phone: form.phone,
		email: form.email,
		userMessage: form.userMessage,

		// Form state
		errors: form.errors,
		meta: form.meta,
		isPhoneDisabled: form.isPhoneDisabled,

		// Form field attributes
		nameAttrs: form.nameAttrs,
		phoneAttrs: form.phoneAttrs,
		emailAttrs: form.emailAttrs,
		userMessageAttrs: form.userMessageAttrs,

		// Form methods
		setFieldValue: form.setFieldValue,
		validate: form.validate,
		handleSubmit: form.handleSubmit,
		setFormSubmittedSuccessfully: form.setFormSubmittedSuccessfully,
		getUserFulfilledPhone: form.getUserFulfilledPhone,

		// Actions
		fetchUserSession,
		getAdCampaign
	};
}
